/**
 * Append-only version history for a tier margin or a base-rate row.
 *
 * Both `/correspondents/admin/tier_margins/{tier}/history` and
 * `/correspondents/admin/rtl_base_rates/{product}/history` return the same
 * append-only entries (oldest first), but with different payloads:
 *
 *   tier margins → { changedBy, changedDateTime, priorEffectiveDate,
 *                    changes: { field: { old, new } } }
 *   base rates   → { changedBy, changedDateTime, priorEffectiveDate,
 *                    priorRatesByTerm: { "12": 0.105, … } }
 *
 * Rendered newest-first, since "what changed last" is the question being asked.
 */
import { useEffect, useState } from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import { AdminEmpty } from "../shared/AdminScreenShell";

const fmt = (v) =>
  v == null ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v);

export default function VersionHistoryDialog({ open, title, fetcher, onClose }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setRows(null);
      return;
    }
    setBusy(true);
    Promise.resolve(fetcher())
      .then((r) => setRows(Array.isArray(r) ? r.slice().reverse() : []))
      .catch(() => setRows([]))
      .finally(() => setBusy(false));
    // `fetcher` closes over the currently-selected row; re-running on every
    // identity change would loop, so the open flag is the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent dividers>
        {busy ? <LinearProgress sx={{ mb: 2 }} /> : null}
        {rows && rows.length === 0 ? (
          <AdminEmpty>No edits recorded yet.</AdminEmpty>
        ) : (
          <Flx column g={1.5}>
            {(rows || []).map((entry, i) => (
              <Box
                key={i}
                sx={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  p: 1.5,
                  bgcolor: "#fff",
                }}
              >
                <Flx ac jb g={1} wrap sx={{ mb: 1 }}>
                  <Txt sx={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>
                    {entry.changedBy || "unknown user"}
                  </Txt>
                  <Txt sx={{ fontSize: 11.5, color: "#64748b" }}>
                    {formatDateTime(entry.changedDateTime) || "—"}
                  </Txt>
                </Flx>

                {entry.changes && Object.keys(entry.changes).length ? (
                  <Flx column g={0.5}>
                    {Object.entries(entry.changes).map(([field, d]) => (
                      <Txt
                        key={field}
                        sx={{ fontSize: 12, color: "#334155", display: "block" }}
                      >
                        <b>{field}</b>: {fmt(d?.old)} → {fmt(d?.new)}
                      </Txt>
                    ))}
                  </Flx>
                ) : entry.priorRatesByTerm ? (
                  <Txt sx={{ fontSize: 12, color: "#334155", display: "block" }}>
                    <b>prior ratesByTerm</b>: {fmt(entry.priorRatesByTerm)}
                  </Txt>
                ) : (
                  <Txt sx={{ fontSize: 12, color: "#94a3b8" }}>
                    No field-level detail recorded for this entry.
                  </Txt>
                )}

                {entry.priorEffectiveDate ? (
                  <Txt
                    sx={{
                      fontSize: 11,
                      color: "#94a3b8",
                      display: "block",
                      mt: 0.75,
                    }}
                  >
                    prior effective {formatDateTime(entry.priorEffectiveDate)}
                  </Txt>
                ) : null}
              </Box>
            ))}
          </Flx>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

VersionHistoryDialog.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.string,
  fetcher: PropTypes.func.isRequired,
  onClose: PropTypes.func,
};
